/**
 * Docs site admin API (Kong → sentrodesk-docs-service).
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { getTenantApiWorkspaceId } from "@/lib/tenantApi";

const PREFIX = "/api/v1/docs-service/workspace/docs-site";

function _path(suffix) {
  return `${PREFIX}${suffix.startsWith("/") ? suffix : `/${suffix}`}`;
}

function _headers(extra = {}) {
  const headers = {
    Accept: "application/json",
    ...getBearerAuthHeaders(),
    ...extra,
  };
  const wid = getTenantApiWorkspaceId();
  if (wid) headers["X-Workspace-Id"] = wid;
  return headers;
}

async function _parseResponse(res) {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg =
      (detail && typeof detail === "object" && detail.message) ||
      (typeof detail === "string" ? detail : null) ||
      data?.message ||
      res.statusText;
    const err = new Error(typeof msg === "string" ? msg : "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchDocsSiteSettings() {
  const res = await fetchWithTimeout(_path("/"), { headers: _headers() });
  return _parseResponse(res);
}

export async function updateDocsSiteSettings(body) {
  const res = await fetchWithTimeout(_path("/"), {
    method: "PUT",
    headers: _headers({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return _parseResponse(res);
}

export async function verifyDocsDomain() {
  const res = await fetchWithTimeout(_path("/domain/verify"), {
    method: "POST",
    headers: _headers(),
  });
  return _parseResponse(res);
}

export async function fetchDocsPages() {
  const res = await fetchWithTimeout(_path("/pages"), { headers: _headers() });
  return _parseResponse(res);
}

export async function createDocsPage(body) {
  const res = await fetchWithTimeout(_path("/pages"), {
    method: "POST",
    headers: _headers({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return _parseResponse(res);
}

export async function fetchDocsPage(pageId) {
  const res = await fetchWithTimeout(_path(`/pages/${pageId}`), { headers: _headers() });
  return _parseResponse(res);
}

export async function updateDocsPage(pageId, body) {
  const res = await fetchWithTimeout(_path(`/pages/${pageId}`), {
    method: "PATCH",
    headers: _headers({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return _parseResponse(res);
}

export async function deleteDocsPage(pageId) {
  const res = await fetchWithTimeout(_path(`/pages/${pageId}`), {
    method: "DELETE",
    headers: _headers(),
  });
  if (res.status === 204) return null;
  return _parseResponse(res);
}

export async function uploadDocsPageDocx(pageId, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetchWithTimeout(_path(`/pages/${pageId}/upload`), {
    method: "POST",
    headers: _headers(),
    body: form,
  });
  return _parseResponse(res);
}

/** Map API hostingMode to UI mode id. */
export function uiHostingModeFromApi(apiMode) {
  return apiMode === "custom_domain" ? "custom" : "sentrodesk";
}

/** Map UI mode id to API hostingMode. */
export function apiHostingModeFromUi(uiMode) {
  return uiMode === "custom" ? "custom_domain" : "sentrodesk";
}
